import { BaseRemoteService } from "@ofqual/portal-core/core/dataAccess";
import {
  Opts,
  QueryParams,
  PagedResponse,
  ValidatedResponse,
  Model,
} from "@ofqual/portal-core/core/types";
import { paramsToSearchString } from "@ofqual/portal-core/core/utils/urlHelper";
import { getValidationError } from "@ofqual/portal-core/core/utils/validationHelper";
import { PendingReturn } from "../domain/PendingReturn";

const toCamelCase = (obj: { [key: string]: string[] }) => {
  const keys = Object.keys(obj);
  const toCamelCaseKey = (key: string) =>
    (key.charAt(0).toLowerCase() + key.slice(1) || key).toString();
  return keys.reduce(
    (acc, curr) => ({ ...acc, [toCamelCaseKey(curr)]: obj[curr] }),
    {}
  );
};

export class DataPortalLookupService extends BaseRemoteService {
  public async get(orgId: number, opts: Opts): Promise<PendingReturn> {
    const url = `${this.apiRoot}/dataportal/pendingReturns/${paramsToSearchString({ orgId })}`;
    return await super.getRequest<PendingReturn>(url, opts);
  }

  public async getArchive(orgId: number, opts: Opts): Promise<PendingReturn> {
    const url = `${this.apiRoot}/dataportal/recentreturns/${paramsToSearchString({ orgId })}`;
    return await super.getRequest<PendingReturn>(url, opts);
  }

  public async getValidationResults(
    id: string,
    opts: Opts
  ): Promise<PendingReturn> {
    const url = `${this.apiRoot}/dataportal/validationresults`;
    return await super.getRequest<PendingReturn>(url, opts);
  }

  public async search(
    params: QueryParams,
    opts: Opts
  ): Promise<PagedResponse<PendingReturn>> {
    const url = `${
      this.apiRoot
    }/dataportal/pendingReturns${paramsToSearchString(params)}`;
    return await super.getRequest<PagedResponse<PendingReturn>>(url, opts);
  }

  public async create(
    item: PendingReturn,
    opts: Opts
  ): Promise<ValidatedResponse<PendingReturn>> {
    const url = `${this.apiRoot}/dataportal/pendingReturns`;
    try {
      const lookupSerialised = JSON.stringify(item);
      const response: PendingReturn = await super.postRequestFormData(
        url,
        { lookupSerialised },
        opts
      );
      return {
        isValid: true,
        data: response,
      };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        const mappedError = {
          ...validationError,
          error: {
            ...validationError.error,
            errors: toCamelCase(validationError.error.errors),
          },
        };
        return { isValid: false, error: mappedError.error };
      } else {
        throw err;
      }
    }
  }

  public async update(
    updates: Array<PendingReturn>,
    opts: Opts
  ): Promise<ValidatedResponse<Array<PendingReturn>>> {
    const url = `${this.apiRoot}/dataportal/pendingReturns`;
    try {
      const lookupsSerialised = JSON.stringify(updates);
      const response: Array<PendingReturn> = await super.putRequestFormData(
        url,
        { lookupsSerialised },
        opts
      );
      return {
        isValid: true,
        data: response,
      };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async delete(id: string, opts: Opts): Promise<void> {
    const url = `${this.apiRoot}/dataportal/pendingReturns${id}`;
    return await super.deleteRequest(url, opts);
  }

  public async submitReturn(fileName: string, collectionAoId: number, templateId: number, opts: Opts): Promise<Model> {
    const url = `${this.apiRoot}/dataportal/submitstandardrequest?filename=${fileName}&collectionAoId=${collectionAoId}&templateId=${templateId}`;
    return await super.postRequest<Model>(url, {}, opts);
  }

  public async submitNilReturn(collectionAoId: number, templateId: number, opts: Opts): Promise<Model> {
    const url = `${this.apiRoot}/dataportal/submitnullrequest?collectionAoId=${collectionAoId}&templateId=${templateId}`;
    return await super.postRequest<Model>(url, {}, opts);
  }
}
